import { ManageInvestorDetailsContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/InvestorDetails';
import {
	applyVestingStartDateToTransaction,
	applyVestingTermToTransaction,
	removeVestingTermFromTransaction,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useMutation } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
	GetInvestorTransactionDetailsRes,
	LoanDefinitionType,
	SecurityType,
} from '@dealstack/shared/lib/generated';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { getTransactionTableColumns } from './transactionTableColumns';
import type { TransactionTableView } from './transactionTable.types';

interface Props {
	getInvestmentReference: (
		investmentWorkflowType: string,
		isReleased: boolean,
		date: string
	) => string;
	getSecurityTypeTranslation: (type?: SecurityType | LoanDefinitionType) => string;
}

export const useTransactionTable = ({
	getInvestmentReference,
	getSecurityTypeTranslation,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const dataOperations = useMantineTableDataOperations();
	const { mipId } = useRequiredParams('mipId');
	const [view, setView] = useState<TransactionTableView>('completed');
	const [selectedTransactionId, setSelectedTransactionId] = useState<string | undefined>();

	const {
		investorDetailsData,
		investments,
		investmentsLoading,
		refreshMIPInvestorDetailsData,
		refetchInvestorValuations,
	} = useContext(ManageInvestorDetailsContext);
	const { vestingTerms, refetchVestingTerms } = useMIPManageContext();

	const applyVestingTermMutation = useMutation({
		mutationFn: applyVestingTermToTransaction,
	});
	const removeVestingTermMutation = useMutation({
		mutationFn: removeVestingTermFromTransaction,
	});
	const applyVestingStartDateMutation = useMutation({
		mutationFn: applyVestingStartDateToTransaction,
	});

	const data = useMemo(() => {
		if (view === 'completed' && investments?.transactionDetails?.length) {
			return investments.transactionDetails;
		}

		if (view === 'pending' && investments?.pendingTransactionDetails?.length) {
			return investments.pendingTransactionDetails;
		}

		return [];
	}, [investments, view]);

	const selectedTransaction = useMemo(
		() =>
			selectedTransactionId ? data.find((t) => t.id === selectedTransactionId) : undefined,
		[data, selectedTransactionId]
	);

	const setSelectedTransaction = (transaction: GetInvestorTransactionDetailsRes | undefined) =>
		setSelectedTransactionId(transaction?.id);

	useEffect(() => {
		if (
			!investments?.transactionDetails?.length &&
			investments?.pendingTransactionDetails?.length
		) {
			setView('pending');
		}
	}, [investments]);

	const onVestingTermApply = (vestingTermId: string, transactionId: string) => {
		applyVestingTermMutation.mutate(
			{ vestingTermId, transactionId },
			{
				onSuccess: () => {
					refreshMIPInvestorDetailsData();
					void refetchInvestorValuations();
					showToastV2({
						title: tMipManage('investors.success'),
						state: 'success',
					});
				},
			}
		);
	};

	const onVestingTermRemove = (transactionId: string) => {
		removeVestingTermMutation.mutate(
			{ transactionId },
			{
				onSuccess: () => {
					refreshMIPInvestorDetailsData();
					void refetchInvestorValuations();
					showToastV2({
						title: tMipManage('investors.success'),
						state: 'success',
					});
				},
			}
		);
	};

	const onVestingStartDateChange = (transactionId: string, startDate: Date | null) => {
		const formattedDate = startDate ? browserDateToMidnightUTC(startDate) : null;

		applyVestingStartDateMutation.mutate(
			{
				transactionId,
				startDate: formattedDate,
			},
			{
				onSuccess: () => {
					refreshMIPInvestorDetailsData();
					void refetchInvestorValuations();
					showToastV2({
						title: tMipManage('investors.success'),
						state: 'success',
					});
				},
			}
		);
	};

	const onEditVestingTerm = (transaction: GetInvestorTransactionDetailsRes) => {
		void refetchVestingTerms();
		setSelectedTransaction(transaction);
	};

	const columns = useMemo(
		() =>
			getTransactionTableColumns({
				tMipManage,
				currency: investorDetailsData?.currency,
				vestingTerms,
				mipId,
				view,
				getInvestmentReference,
				getSecurityTypeTranslation,
				onEditVestingTerm,
			}),
		[
			tMipManage,
			investorDetailsData?.currency,
			vestingTerms,
			mipId,
			view,
			getInvestmentReference,
			getSecurityTypeTranslation,
		]
	);

	return {
		investorDetailsData,
		data,
		investmentsLoading,
		columns,
		vestingTerms,
		view,
		setView,
		showViewSwitch: !!investments?.pendingTransactionDetails?.length,
		onVestingTermApply,
		onVestingTermRemove,
		onVestingStartDateChange,
		vestingTermUpdateLoading:
			applyVestingTermMutation.isPending ??
			removeVestingTermMutation.isPending ??
			applyVestingStartDateMutation.isPending,
		selectedTransaction,
		setSelectedTransaction,
		tMipManage,
		dataOperations,
	};
};
